// The casino pane of a desktop (R2198, ui-map-interface.md §12.4).
//
// The owner's ruling of 2026-10-14: a desktop opens ECONOMY · CASINO as a
// wide pane over the map, the desktop form of card 01 of mockup round
// 101, with the board at the left and the table and the wheel stage beside
// it. The desktop casino setting `casinoDesk` keeps the choice: UNDOCKED
// (the default) stands the casino pane over the map, and DOCKED stands the
// casino in the dock column, as before. UNDOCK and DOCK switch it with the
// press of the chat (`dockPressHtml` of `ui/arrowpress.ts`, R1866). A
// telephone keeps the sheet and reads no setting, as the chat band.
//
// Pure rules and markup: the overlay reads the setting and the form
// factor, and places the pane; `casino.ts` draws into its host.

import { overlay as S } from '../../gen/strings';
import { crossGlyph, dockPressHtml } from '../arrowpress';
import { esc } from '../esc';
import type { CasinoDesk } from '../settings';

/** The form the casino takes: the sheet of a telephone, the dock column,
 *  or the casino pane over the map. */
export type CasinoForm = 'sheet' | 'column' | 'pane';

/** The form of the casino on this form factor under this setting. A
 *  telephone always takes the sheet. */
export function casinoForm(sheet: boolean, desk: CasinoDesk): CasinoForm {
  if (sheet) return 'sheet';
  return desk === 'docked' ? 'column' : 'pane';
}

/** UNDOCK of the head in the dock column. */
const UNDOCK = (): string => dockPressHtml({
  way: 'undock', act: 'casinoundock', word: S.chat_undock, title: S.casino_undock_title, cls: 'cpdock',
});

/** DOCK of the head of the pane. */
const DOCK = (): string => dockPressHtml({
  way: 'dock', act: 'casinodock', word: S.chat_dock, title: S.casino_dock_title, cls: 'cpdock',
});

/**
 * The tab body of ECONOMY · CASINO: the casino head and the host the panel
 * draws into, or, while the pane stands, the head and the away line. The
 * head of the dock column carries UNDOCK; the sheet's carries no press.
 * Each form returns one constant string, so the 4 Hz guard of the body
 * sees no change while the casino plays.
 */
export function casinoTabHtml(form: CasinoForm): string {
  if (form === 'pane') {
    return `<h3 class="casino-h">${S.casino_head}</h3>`
      + `<div class="casino-away">${esc(S.casino_pane_away)}</div>`;
  }
  if (form === 'sheet') return `<h3>${S.casino_head}</h3><div class="casino-host"></div>`;
  return `<h3 class="casino-h">${S.casino_head}${UNDOCK()}</h3><div class="casino-host"></div>`;
}

/** The head of the casino pane: the name, DOCK and the close, which leaves
 *  the casino. Built once. */
export function paneHeadHtml(): string {
  return `<h3 class="cp-name">${S.casino_head}</h3>${DOCK()}`
    + `<button class="cpclose sqp" data-act="casinoclose" title="${esc(S.casino_close_title)}"`
    + ` aria-label="${esc(S.casino_close_title)}">${crossGlyph()}</button>`;
}
